/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Frequently Asked Questions section for AI TikTok Video Generator
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`border border-[#CDCDCD] transition-all duration-300 cursor-pointer ${
        isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'
      }`}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        {/* Question */}
        <div className="flex items-center justify-between gap-4">
          <h3 className="flex-1 text-base pad:text-base font-semibold text-[#0F1117]">
            {item.question}
          </h3>
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Answer */}
        <div
          className={`overflow-hidden transition-all duration-300 ${
            isExpanded ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'What is FalcoCut?',
      answer:
        'FalcoCut is an AI-powered video creation platform designed for short-form content. It helps you turn scripts, product links, images, or existing clips into ready-to-publish videos for platforms like TikTok — without manual editing.',
    },
    {
      id: 'faq-2',
      question: 'Can I make TikTok videos without filming myself?',
      answer:
        'Yes. You can create videos using AI avatars, voiceovers, generated visuals, or existing media. No camera, studio, or on-screen appearance is required.',
    },
    {
      id: 'faq-3',
      question: 'How do I create a video with FalcoCut?',
      answer:
        'Start by entering a script, pasting a product or webpage link, or uploading your own assets. FalcoCut generates a video draft that you can customize before exporting.',
    },
    {
      id: 'faq-4',
      question: 'Do I need editing experience?',
      answer:
        "No. FalcoCut is built for non-editors. You don't need prior video editing skills to create polished, platform-ready videos.",
    },
    {
      id: 'faq-5',
      question: 'Can I upload my own clips and images?',
      answer:
        'Yes. You can upload your own videos, images, and audio files, and combine them with AI-generated content inside FalcoCut.',
    },
    {
      id: 'faq-6',
      question: 'Can I choose the style and music?',
      answer:
        'Yes. You can select different visual styles, pacing, and background music to match your content and audience.',
    },
    {
      id: 'faq-7',
      question: 'How long does it take to generate a video?',
      answer:
        'Most videos are generated within a few minutes, depending on length, assets, and selected features.',
    },
    {
      id: 'faq-8',
      question: 'Can I edit after the video is generated?',
      answer:
        'Yes. After generation, you can refine scenes, visuals, scripts, music, and other elements before exporting.',
    },
    {
      id: 'faq-9',
      question: 'Is there a free plan?',
      answer:
        'FalcoCut offers a free plan so you can try the platform and explore its core features. Paid plans unlock higher limits and advanced capabilities.',
    },
    {
      id: 'faq-10',
      question: 'Can I batch-create videos?',
      answer:
        'Yes. FalcoCut supports batch creation, allowing you to generate multiple videos from the same idea, link, or assets efficiently.',
    },
    {
      id: 'faq-11',
      question: 'Can I use the content I create for ads or commercial projects?',
      answer:
        'Yes. Videos created with FalcoCut can be used for commercial purposes, including ads, marketing campaigns, and client projects, according to your plan.',
    },
    {
      id: 'faq-12',
      question: 'Is my likeness and data safe?',
      answer:
        'Yes. FalcoCut prioritizes data security and user privacy. Your uploaded content and generated assets are protected and not shared without your permission.',
    },
  ];

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        {/* FAQ List */}
        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
